import { scaleLinear, scaleLog } from 'd3-scale';
import { maxBy, minBy } from 'lodash-es';

import type { ThresholdMarker } from '../components/threshold-bar/types.js';
import { getMarkerValue } from '../components/threshold-bar/utils/get-marker-value.js';
import { getThresholdPixel } from '../components/threshold-bar/utils/get-threshold-pixel.ts.js';
import { THRESHOLD } from '../messages.js';
import type { FormatterWithPrecision } from '../types/formatter.js';
import type { MinMax } from '../types/min-max.js';
import type { AnyNumberScale, NumericScales } from '../types/scales.js';
import type {
  DateThresholdConfig,
  DynamicNumericRangeThreshold,
  DynamicNumericThreshold,
  DynamicRangeThreshold,
  DynamicThreshold,
  NumericalThresholdConfig,
  StaticRangeThreshold,
  StaticThreshold,
  ThresholdPosition,
} from '../types/thresholds.js';
import {
  isDynamic,
  isDynamicNumeric,
  isStatic,
  isStaticRange,
} from '../types/type-guards.js';
import type { WithId } from '../types/with-id.js';
import { isSymlogScale } from './detect-scale.js';

type SingleThresholdItem = StaticThreshold | StaticRangeThreshold;

type DynamicThresholdItem =
  | DynamicThreshold[number]
  | DynamicRangeThreshold[number]
  | DynamicNumericThreshold[number]
  | DynamicNumericRangeThreshold[number];

function findMinValue(thresholds: DynamicThreshold | DynamicNumericThreshold) {
  return thresholds.reduce(
    (min, current) => (current.value < min.value ? current : min),
    thresholds[0],
  );
}

function findMaxValue(thresholds: DynamicThreshold | DynamicNumericThreshold) {
  return thresholds.reduce(
    (max, current) => (current.value > max.value ? current : max),
    thresholds[0],
  );
}

function findMinRange(
  thresholds: DynamicRangeThreshold | DynamicNumericRangeThreshold,
) {
  return thresholds.reduce(
    (min, current) => (current.min < min.min ? current : min),
    thresholds[0],
  );
}

function findMaxRange(
  thresholds: DynamicRangeThreshold | DynamicNumericRangeThreshold,
) {
  return thresholds.reduce(
    (max, current) => (current.max > max.max ? current : max),
    thresholds[0],
  );
}

const isDataRange = (obj: SingleThresholdItem | DynamicThresholdItem) => {
  return (
    typeof obj === 'object' && obj !== null && 'min' in obj && 'max' in obj
  );
};

const getYValue = (
  data: SingleThresholdItem | DynamicThresholdItem,
  type: 'min' | 'max',
) => {
  if (isDataRange(data)) {
    return data[type];
  }

  return data.value;
};

function findPointWithMinX(data: DynamicThresholdItem[]) {
  return (
    minBy(data, (threshold) =>
      ('point' in threshold ? threshold.point : threshold.date)?.valueOf(),
    ) ?? data[0]
  );
}

function findPointWithMaxX(data: DynamicThresholdItem[]) {
  return (
    maxBy(data, (threshold) =>
      ('point' in threshold ? threshold.point : threshold.date)?.valueOf(),
    ) ?? data[data.length - 1]
  );
}

function getYRangeFromPoint(threshold: DynamicThresholdItem) {
  return {
    y0: getYValue(threshold, 'min'),
    y1: getYValue(threshold, 'max'),
  };
}

function interpolateYRange(
  data: DynamicThresholdItem[],
  xScale: AnyNumberScale,
  targetX: number,
) {
  const xValues = data.map((threshold) =>
    Number('point' in threshold ? threshold.point : threshold.date),
  );
  const minYValues = data.map((threshold) => getYValue(threshold, 'min'));
  const maxYValues = data.map((threshold) => getYValue(threshold, 'max'));
  const minYScale = isSymlogScale(xScale)
    ? scaleLog(xValues, minYValues).clamp(true)
    : scaleLinear(xValues, minYValues).clamp(true);
  const maxYScale = isSymlogScale(xScale)
    ? scaleLog(xValues, maxYValues).clamp(true)
    : scaleLinear(xValues, maxYValues).clamp(true);

  return {
    y0: minYScale(targetX),
    y1: maxYScale(targetX),
  };
}

function getDynamicThresholdDomain(
  data: DynamicThresholdItem[],
  side: ThresholdPosition,
  visibleDomain: MinMax,
  xScale: AnyNumberScale,
) {
  const minXPoint = findPointWithMinX(data);
  const maxXPoint = findPointWithMaxX(data);

  const targetXDomain = side === 'left' ? visibleDomain.min : visibleDomain.max;

  const minPointValue =
    ('point' in minXPoint ? minXPoint.point : minXPoint.date)?.valueOf() ?? 0;
  const maxPointValue =
    ('point' in maxXPoint ? maxXPoint.point : maxXPoint.date)?.valueOf() ?? 0;

  if (targetXDomain <= minPointValue) {
    return getYRangeFromPoint(minXPoint);
  }
  if (targetXDomain >= maxPointValue) {
    return getYRangeFromPoint(maxXPoint);
  }

  return interpolateYRange(data, xScale, targetXDomain);
}

const buildShadowMarker = (
  threshold: WithId<DateThresholdConfig> | WithId<NumericalThresholdConfig>,
  yScale: NumericScales,
) => {
  let minThreshold;
  let maxThreshold;

  if (isDynamic(threshold.data) || isDynamicNumeric(threshold.data)) {
    minThreshold = findMinValue(threshold.data);
    maxThreshold = findMaxValue(threshold.data);
  } else {
    minThreshold = findMinRange(
      threshold.data as DynamicRangeThreshold | DynamicNumericRangeThreshold,
    );
    maxThreshold = findMaxRange(
      threshold.data as DynamicRangeThreshold | DynamicNumericRangeThreshold,
    );
  }

  return {
    ...threshold,
    y0: getThresholdPixel(getYValue(minThreshold, 'min'), yScale),
    y1: getThresholdPixel(getYValue(maxThreshold, 'max'), yScale),
    value: THRESHOLD.dynamic.defaultMessage,
    isShadow: true,
  };
};

const buildMarker = (
  threshold: WithId<DateThresholdConfig> | WithId<NumericalThresholdConfig>,
  thresholdBarPosition: ThresholdPosition,
  xScale: AnyNumberScale,
  yScale: NumericScales,
) => {
  const domain = xScale.domain();
  const xMinMax = {
    min: domain[0].valueOf(),
    max: domain[1].valueOf(),
  };

  const { y0, y1 } = getDynamicThresholdDomain(
    threshold.data as
      | DynamicThreshold
      | DynamicNumericThreshold
      | DynamicRangeThreshold
      | DynamicNumericRangeThreshold,
    thresholdBarPosition,
    xMinMax,
    xScale,
  );

  return {
    ...threshold,
    y0: getThresholdPixel(y0, yScale),
    y1: getThresholdPixel(y1, yScale),
    value: THRESHOLD.dynamic.defaultMessage,
  };
};

export const transformThresholdsToMarkers =
  (
    thresholdBarPosition: ThresholdPosition,
    xScale: AnyNumberScale,
    yScale: NumericScales,
    formatter: FormatterWithPrecision,
  ) =>
  (
    acc: ThresholdMarker[],
    threshold: WithId<DateThresholdConfig> | WithId<NumericalThresholdConfig>,
  ) => {
    if (thresholdBarPosition === threshold.position) {
      if (isStatic(threshold.data) || isStaticRange(threshold.data)) {
        const y0Value = getYValue(threshold.data, 'min');
        const y1Value = getYValue(threshold.data, 'max');

        acc.push({
          ...threshold,
          y0: getThresholdPixel(y0Value, yScale),
          y1: getThresholdPixel(y1Value, yScale),
          value: getMarkerValue({ y0: y0Value, y1: y1Value }, formatter),
        });
      } else {
        const shadowMarker = buildShadowMarker(threshold, yScale);
        acc.push(shadowMarker);

        const marker = buildMarker(
          threshold,
          thresholdBarPosition,
          xScale,
          yScale,
        );
        acc.push(marker);
      }
    }

    return acc;
  };
